import { IntlMessageFormat } from 'intl-messageformat';

export const translatableAttributes = Object.freeze(['aria-label', 'title', 'placeholder', 'alt', 'content', 'data-label', 'data-model', 'data-best', 'data-inside', 'data-outside', 'data-outside-sub']);
export const runtimeMessageKeys = Object.freeze(['language.current', 'language.choose', 'language.search', 'language.label', 'language.empty', 'theme.darkMode', 'theme.switchToLight', 'theme.switchToDark', 'navigation.openMenu', 'navigation.closeMenu', "booking.duration", "booking.selectPrompt", "booking.selection", "booking.dayLabel", "booking.loading", "booking.chooseTime", "booking.noSlots", "booking.loadFailed", "booking.required", "booking.termsRequired", "booking.emailInvalid", "booking.submitting", "booking.unavailable", "booking.rateLimit", "booking.uncertain", "booking.bookingFailed", "booking.successTitle", "booking.successCopy", "booking.pendingTitle", "booking.pendingCopy", "booking.topic.consultation", "booking.topic.review", "booking.topic.planning"]);

export function matchLocale(value, languages) {
  if (typeof value !== 'string' || !value.trim()) return null;
  let canonical;
  try { [canonical] = Intl.getCanonicalLocales(value.trim().replaceAll('_', '-')); }
  catch { return null; }
  const exact = languages.find(language => language.code.toLowerCase() === canonical.toLowerCase());
  const base = canonical.split('-')[0].toLowerCase();
  return exact?.code ?? languages.find(language => language.code.split('-')[0].toLowerCase() === base)?.code ?? null;
}

export function resolveLocale({ query, saved, browser = [], languages, defaultLocale }) {
  return matchLocale(query, languages) ?? matchLocale(saved, languages)
    ?? browser.map(value => matchLocale(value, languages)).find(Boolean) ?? defaultLocale;
}

export function createTranslator({ locale, messages, fallback, protectedTerms }) {
  const fixed = Object.freeze({ ...protectedTerms });
  const formatters = new Map();
  function format(key, values = {}, renderArgument) {
    const message = typeof messages[key] === 'string' ? messages[key] : fallback[key];
    if (typeof message !== 'string') throw new Error(`Unknown translation key: ${key}`);
    const messageLocale = typeof messages[key] === 'string' ? locale : 'en';
    const cacheKey = `${messageLocale}:${key}`;
    if (!formatters.has(cacheKey)) {
      const formatter = new IntlMessageFormat(message, messageLocale);
      const types = new Map();
      function collect(nodes) {
        for (const node of nodes) {
          if (node.type >= 1 && node.type <= 6) {
            const set = types.get(node.value) ?? new Set();
            set.add(node.type); types.set(node.value, set);
          }
          if (node.children) collect(node.children);
          if (node.options) for (const option of Object.values(node.options)) collect(option.value);
        }
      }
      collect(formatter.getAst());
      const plainArguments = [...types].filter(([, set]) => set.size === 1 && set.has(1)).map(([name]) => name);
      formatters.set(cacheKey, { formatter, plainArguments });
    }
    const { formatter, plainArguments } = formatters.get(cacheKey);
    const resolved = { year: String(new Date().getFullYear()), ...values, ...fixed };
    if (renderArgument) for (const name of plainArguments) {
      if (typeof resolved[name] === 'string' || typeof resolved[name] === 'number') {
        resolved[name] = renderArgument(resolved[name], { name, protected: name in fixed });
      }
    }
    return formatter.format(resolved);
  }
  return Object.freeze({
    format,
    // Markup belongs to the renderer, never the catalogue. Numeric/select/plural
    // operands remain raw so ICU can choose CLDR categories and format values.
    formatMarkup: (key, values, renderArgument) => format(key, values, renderArgument),
    text(key, values) {
      const result = format(key, values);
      if (typeof result !== 'string' && typeof result !== 'number') throw new Error(`Rich message requires slots: ${key}`);
      return String(result);
    },
    number: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    currency: (value, currency, options) => new Intl.NumberFormat(locale, { ...options, style: 'currency', currency }).format(value),
    percent: (value, options) => new Intl.NumberFormat(locale, { ...options, style: 'percent' }).format(value),
    date: (value, options) => new Intl.DateTimeFormat(locale, options).format(value),
    relativeTime: (value, unit, options) => new Intl.RelativeTimeFormat(locale, options).format(value, unit),
    list: (values, options) => new Intl.ListFormat(locale, options).format(values),
  });
}

export const escapeHtml = value => String(value).replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
})[character]);

// Keep a sentence containing isolated arguments as one inline text run. Without
// this grouping, flex/grid containers treat each argument as a separate item.
export function groupInlineArguments(parts, isArgument, wrap) {
  const result = [], run = [];
  function flush() {
    if (run.length > 1 && run.some(isArgument)) result.push(wrap([...run]));
    else result.push(...run);
    run.length = 0;
  }
  for (const part of Array.isArray(parts) ? parts : [parts]) {
    if (typeof part !== 'object' || isArgument(part)) run.push(part);
    else { flush(); result.push(part); }
  }
  flush();
  return result;
}

export function messageSchema(message, locale = 'en') {
  const argumentsByType = new Map();
  const tags = new Set();
  function visit(nodes) {
    for (const node of nodes) {
      if (node.type === 8) { tags.add(node.value); visit(node.children); }
      else if (node.type >= 1 && node.type <= 6) {
        const types = argumentsByType.get(node.value) ?? new Set();
        types.add(node.type); argumentsByType.set(node.value, types);
      }
      if (node.options) for (const option of Object.values(node.options)) visit(option.value);
    }
  }
  const ast = new IntlMessageFormat(message, locale).getAst();
  function checkSlots(nodes, previous = new Set()) {
    const seen = new Set(previous);
    for (const node of nodes) {
      if (node.type === 8) {
        if (seen.has(node.value)) throw new Error(`Repeated rich slot: ${node.value}`);
        seen.add(node.value);
        for (const name of checkSlots(node.children, seen)) seen.add(name);
      }
      if (node.options) {
        const alternatives = Object.values(node.options).map(option => checkSlots(option.value, seen));
        for (const branch of alternatives) for (const name of branch) seen.add(name);
      }
    }
    return seen;
  }
  checkSlots(ast);
  visit(ast);
  return { arguments: [...argumentsByType].map(([key, types]) => [key, [...types].sort()]).sort(), tags: [...tags].sort() };
}

export function validateCatalog(locale, catalog, english, protectedTerms) {
  if (!catalog || typeof catalog !== 'object' || Array.isArray(catalog)) throw new Error(`Invalid catalogue: ${locale}`);
  const expected = Object.keys(english).sort();
  if (JSON.stringify(Object.keys(catalog).sort()) !== JSON.stringify(expected)) throw new Error(`Catalogue key mismatch: ${locale}`);
  for (const key of expected) {
    if (typeof catalog[key] !== 'string' || !catalog[key].trim()) throw new Error(`Missing message: ${locale}.${key}`);
    const expectedSchema = messageSchema(english[key]);
    if (JSON.stringify(messageSchema(catalog[key], locale)) !== JSON.stringify(expectedSchema)) {
      throw new Error(`Placeholder or rich-slot mismatch: ${locale}.${key}`);
    }
    for (const [term, literal] of Object.entries(protectedTerms)) {
      const schema = expectedSchema;
      if (schema.arguments.some(([name]) => name === term) && !schema.arguments.find(([name]) => name === term)[1].every(type => type === 1)) {
        throw new Error(`Protected name must remain a plain placeholder: ${key}.${term}`);
      }
      // A translated catalogue must reference immutable names through their placeholders.
      if (locale !== 'en' && containsProtectedLiteral(catalog[key], literal) && !containsProtectedLiteral(english[key], literal)) throw new Error(`Literal protected name: ${locale}.${key}.${term}`);
    }
  }
}

// Inline SVG optimisers can strip data attributes. A stable class also identifies its message.
export function svgMessageKey(className = '') {
  const token = className.split(/\s+/).find(value => value.startsWith('an-i18n-svg--'));
  return token ? token.slice('an-i18n-svg--'.length).replaceAll('__', '.') : null;
}

export function slotName(className = '') {
  return className.split(/\s+/).find(value => value.startsWith('an-i18n-slot--'))?.slice('an-i18n-slot--'.length) ?? null;
}

function containsProtectedLiteral(message, literal) {
  const escaped = literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`, 'u').test(message);
}
